import axios, { AxiosError, AxiosResponse } from 'axios';
import { Task, Test, Evaluation, TaskResult } from '@/types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5500/api';
const ENV = process.env.REACT_APP_ENV || 'development';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Send cookies with requests
});

/*
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
*/

export { API_BASE_URL };  // Export for use in other parts of the application

// Define possible error data structures
export interface ApiErrorData {
  message?: string;
  tests?: Array<{ name: string; [key: string]: any }>;
  evaluations?: Array<{ modelName: string; [key: string]: any }>;
  [key: string]: any; // For other potential properties
}

// Custom error type with additional data
export interface ApiError extends Error {
  status?: number;
  data?: ApiErrorData;
}

// Helper function to handle API errors
const handleApiError = (error: unknown): ApiError => {
  // Start with a generic message
  let errorMessage = 'An unknown error occurred';

  // Create a base error object
  const apiError: ApiError = new Error();
  
  if (axios.isAxiosError(error) && error.response) {
    // If it's an Axios error with a response, use the server's message
    errorMessage = error.response.data?.message || error.message;
    apiError.status = error.response.status;
    apiError.data = error.response.data as ApiErrorData;
  } else if (error instanceof Error) {
    // For other types of errors
    errorMessage = error.message;
  }

  apiError.message = errorMessage;

  // Log more detailed errors in development environment
  if (ENV === 'development') {
    console.error('API Error:', error);
  }
  
  return apiError;
};

export const api = {
  auth: {
    login: async (credentials: {email: string, password: string}): Promise<any> => {
      try {
        const response = await apiClient.post(`/auth/login`, credentials);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },
    getMe: async (): Promise<any> => {
      try {
        const response = await apiClient.get(`/auth/me`);
        return response.data;
      } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          return null; // A 401 here just means the user is not logged in.
        }
        throw handleApiError(error);
      }
    },
    logout: async (): Promise<void> => {
      try {
        await apiClient.post(`/auth/logout`);
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    }
  },
  public: {
    getPublicTests: async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/public/tests`);
        return data.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    getPublicResults: async (testId: string) => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/public/results/${testId}`);
        return data.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    getAppConfig: async (): Promise<{ wordpressSsoUrl: string }> => {
      try {
        const { data } = await apiClient.get('/public/config');
        return data.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
  },
  dashboard: {
    getDashboardTests: async () => {
      try {
        const { data } = await apiClient.get('/dashboard/tests');
        return data.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
    getDashboardResults: async (testId: string) => {
      try {
        const { data } = await apiClient.get(`/dashboard/results/${testId}`);
        return data.data;
      } catch (error) {
        throw handleApiError(error);
      }
    },
  },
  tasks: {
    getAll: async (): Promise<Task[]> => {
      try {
        const response = await apiClient.get(`/tasks`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    getById: async (id: string): Promise<Task> => {
      try {
        const response = await apiClient.get(`/tasks/${id}`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    getCategories: async (): Promise<string[]> => {
      try {
        const response = await apiClient.get(`/tasks/categories`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    create: async (taskData: FormData | Omit<Task, '_id' | 'createdAt' | 'updatedAt'>): Promise<Task> => {
      try {
        const headers = taskData instanceof FormData 
          ? { 'Content-Type': 'multipart/form-data' }
          : { 'Content-Type': 'application/json' };
          
        const response = await apiClient.post(`/tasks`, taskData, { headers });
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    update: async (id: string, taskData: FormData | Partial<Task>): Promise<Task> => {
      try {
        const headers = taskData instanceof FormData 
          ? { 'Content-Type': 'multipart/form-data' }
          : { 'Content-Type': 'application/json' };
          
        const response = await apiClient.put(`/tasks/${id}`, taskData, { headers });
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    addAttachments: async (id: string, attachments: FormData): Promise<Task> => {
      try {
        const headers = { 'Content-Type': 'multipart/form-data' };
        const response = await apiClient.post(`/tasks/${id}/attachments`, attachments, { headers });
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    delete: async (id: string): Promise<void> => {
      try {
        await apiClient.delete(`/tasks/${id}`);
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    // Add new method for getting attachment URLs
    getAttachmentUrl: (taskId: string, attachmentId: string): string => {
      return `${API_BASE_URL}/tasks/${taskId}/attachments/${attachmentId}`;
    }
  },

  user: {
    getEvaluationById: async (id: string): Promise<Evaluation> => {
      try {
        const response = await apiClient.get(`/user/evaluations/${id}`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },
  },

  // Test API
  tests: {
    getAll: async (): Promise<Test[]> => {
      try {
        const response = await apiClient.get(`/tests`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    getById: async (id: string): Promise<Test> => {
      try {
        const response = await apiClient.get(`/tests/${id}`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    create: async (test: Omit<Test, '_id' | 'createdAt' | 'updatedAt' | 'tasks'>): Promise<Test> => {
      try {
        const response = await apiClient.post(`/tests`, test);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    update: async (id: string, test: Partial<Test>): Promise<Test> => {
      try {
        const response = await apiClient.put(`/tests/${id}`, test);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    updatePriority: async (id: string, priority: number): Promise<Test> => {
      try {
        const response = await apiClient.patch(`/tests/${id}/priority`, { priority });
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    delete: async (id: string): Promise<void> => {
      try {
        await apiClient.delete(`/tests/${id}`);
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    addTask: async (testId: string, taskId: string): Promise<Test> => {
      try {
        const response = await apiClient.post(`/tests/${testId}/tasks`, { taskId });
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    removeTask: async (testId: string, taskId: string): Promise<void> => {
      try {
        await apiClient.delete(`/tests/${testId}/tasks/${taskId}`);
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    }
  },

  // Evaluation API
  evaluations: {
    getAll: async (): Promise<Evaluation[]> => {
      try {
        const response = await apiClient.get(`/evaluations`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    getById: async (id: string): Promise<Evaluation> => {
      try {
        const response = await apiClient.get(`/evaluations/${id}`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    getModelNames: async (): Promise<string[]> => {
      try {
        const response = await apiClient.get(`/evaluations/models`);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    create: async (evaluation: Omit<Evaluation, '_id' | 'createdAt' | 'updatedAt' | 'results' | 'isCompleted'>): Promise<Evaluation> => {
      try {
        const response = await apiClient.post(`/evaluations`, evaluation);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    update: async (id: string, evaluation: Partial<Evaluation>): Promise<Evaluation> => {
      try {
        const response = await apiClient.put(`/evaluations/${id}`, evaluation);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    delete: async (id: string): Promise<void> => {
      try {
        await apiClient.delete(`/evaluations/${id}`);
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    addResult: async (evaluationId: string, result: Omit<TaskResult, '_id'>): Promise<Evaluation> => {
      try {
        const response = await apiClient.post(`/evaluations/${evaluationId}/results`, result);
        return response.data;
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    },

    complete: async (evaluationId: string): Promise<void> => {
      try {
        await apiClient.put(`/evaluations/${evaluationId}/complete`);
      } catch (error: unknown) {
        throw handleApiError(error);
      }
    }
  },
};

export default api; 